import React,{useEffect,useState} from 'react';
import {motion} from 'framer-motion';
import {useSupabaseData} from '../../contexts/SupabaseDataContext';
import {getSellerPath} from '../../services/storageService';
import {getShopUrl,isValidShopUsername,normalizeShopUsername} from '../../services/shopLinkService';
import {saveSellerProfile} from '../../services/sellerProfileService';
import MultiImageManager from '../common/MultiImageManager';
import TaxInformationSection from './TaxInformationSection';
import SellerAvailabilityEditor from './SellerAvailabilityEditor';
import useSellerPlanAccess from '../../hooks/useSellerPlanAccess';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiLoader,
  FiMapPin,
  FiSave,
  FiTruck,
  FiCalendar
}=FiIcons;

const SellerProfileTab=({seller,userId})=>{
  const {refreshData}=useSupabaseData();
  const {hasFeature:hasPlanFeature}=useSellerPlanAccess(seller?.id);
  const [formData,setFormData]=useState({
    business_name:'',
    description:'',
    location:'',
    category:'',
    availability:'',
    profile_images:[],
    shop_username:'',
    is_delivery_enabled:false,
    delivery_charge:'',
    min_order_window:1,
    weekly_availability:null,
    blackout_dates:[],
    pickup_weekly_availability:null,
    pickup_blackout_dates:[]
  });
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [success,setSuccess]=useState(false);

  useEffect(()=>{
    if (!seller) return;

    setFormData({
      business_name:seller.business_name || '',
      description:seller.description || '',
      location:seller.location || '',
      category:seller.category || '',
      availability:seller.availability || '',
      profile_images:seller.profile_images?.length
        ? seller.profile_images
        : seller.profile_image
          ? [seller.profile_image]
          : [],
      shop_username:seller.shop_username || '',
      is_delivery_enabled:Boolean(seller.is_delivery_enabled),
      delivery_charge:seller.delivery_charge ?? '',
      min_order_window:seller.min_order_window ?? 1,
      weekly_availability:seller.weekly_availability || null,
      blackout_dates:Array.isArray(seller.blackout_dates)
        ? seller.blackout_dates
        : [],
      pickup_weekly_availability:seller.pickup_weekly_availability || null,
      pickup_blackout_dates:Array.isArray(seller.pickup_blackout_dates)
        ? seller.pickup_blackout_dates
        : []
    });
  },[seller]);

  const updateField=(field,value)=>{
    setFormData((current)=>({...current,[field]:value}));
  };

  const handleSubmit=async (event)=>{
    event.preventDefault();
    if (!seller || loading) return;

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const shopUsername=normalizeShopUsername(formData.shop_username);

      if (shopUsername && !isValidShopUsername(shopUsername)) {
        throw new Error('Choose 3–30 lowercase letters, numbers, or hyphens.');
      }

      await saveSellerProfile(seller.id,{
        business_name:formData.business_name.trim(),
        description:formData.description.trim(),
        location:formData.location.trim(),
        category:formData.category.trim(),
        availability:formData.availability.trim(),
        profile_images:formData.profile_images,
        profile_image:formData.profile_images[0] || null,
        shop_username:shopUsername || null,
        is_delivery_enabled:formData.is_delivery_enabled,
        delivery_charge:formData.is_delivery_enabled
          ? Number(formData.delivery_charge) || 0
          : 0,
        min_order_window:Math.max(
          0,
          Number(formData.min_order_window) || 0
        ),
        weekly_availability:formData.weekly_availability,
        blackout_dates:[...new Set(formData.blackout_dates)].sort(),
        pickup_weekly_availability:formData.pickup_weekly_availability,
        pickup_blackout_dates:[
          ...new Set(formData.pickup_blackout_dates)
        ].sort()
      });

      await refreshData();
      setSuccess(true);
      window.setTimeout(()=>setSuccess(false),3000);
    } catch (saveError) {
      setError(saveError.message || 'Unable to save your seller profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-10">
        <h2 className="text-3xl font-black text-slate-900 tracking-tighter">
          Store <span className="text-indigo-600">Profile</span>
        </h2>
        <p className="mt-2 text-slate-500 font-medium">
          Tell neighbors about your business, availability, and fulfillment options.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="flex items-center rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
            <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" />
            {error}
          </div>
        )}

        {success && (
          <motion.div
            initial={{opacity:0,y:8}}
            animate={{opacity:1,y:0}}
            className="flex items-center rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-700"
          >
            <SafeIcon icon={FiCheckCircle} className="mr-3 text-lg" />
            Your seller profile has been updated.
          </motion.div>
        )}

        <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-sm md:p-10">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-3 ml-1 block text-xs font-black uppercase tracking-widest text-slate-400">
                Business Name
              </label>
              <input
                type="text"
                required
                value={formData.business_name}
                onChange={(event)=>updateField('business_name',event.target.value)}
                className="w-full rounded-[1.5rem] border border-transparent bg-slate-50 px-7 py-5 text-lg font-bold outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500"
                placeholder="Your bakery or business name"
              />
            </div>

            <div>
              <label className="mb-3 ml-1 block text-xs font-black uppercase tracking-widest text-slate-400">
                Category
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(event)=>updateField('category',event.target.value)}
                className="w-full rounded-2xl border border-transparent bg-slate-50 px-6 py-4 font-bold outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g. Cakes"
              />
            </div>

            <div>
              <label className="mb-3 ml-1 block text-xs font-black uppercase tracking-widest text-slate-400">
                Location
              </label>
              <div className="relative">
                <SafeIcon
                  icon={FiMapPin}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"
                />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(event)=>updateField('location',event.target.value)}
                  className="w-full rounded-2xl border border-transparent bg-slate-50 py-4 pl-12 pr-6 font-bold outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="City, Province"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="mb-3 ml-1 block text-xs font-black uppercase tracking-widest text-slate-400">
                Availability
              </label>
              <input
                type="text"
                value={formData.availability}
                onChange={(event)=>updateField('availability',event.target.value)}
                className="w-full rounded-2xl border border-transparent bg-slate-50 px-6 py-4 font-bold outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g. Monday–Friday, 9 AM–6 PM"
              />
            </div>

            <div className="md:col-span-2 rounded-[2rem] border border-indigo-100 bg-indigo-50/50 p-6">
              <div className="mb-5 flex items-center">
                <SafeIcon icon={FiTruck} className="mr-3 text-xl text-indigo-600" />
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-indigo-900">
                    Delivery Settings
                  </h3>
                  <p className="mt-1 text-xs font-medium text-indigo-500">
                    Tell buyers how you fulfill orders.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="flex items-center rounded-2xl bg-white p-4 font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={formData.is_delivery_enabled}
                    onChange={(event)=>updateField(
                      'is_delivery_enabled',
                      event.target.checked
                    )}
                    className="mr-3 h-5 w-5 rounded-lg text-indigo-600 focus:ring-indigo-500"
                  />
                  I offer delivery
                </label>

                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Delivery Cost
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={!formData.is_delivery_enabled}
                    value={formData.delivery_charge}
                    onChange={(event)=>updateField(
                      'delivery_charge',
                      event.target.value
                    )}
                    className="w-full rounded-2xl border border-transparent bg-white px-5 py-4 font-bold outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Minimum Notice
                  </label>
                  <div className="relative">
                    <SafeIcon
                      icon={FiCalendar}
                      className="absolute left-5 top-1/2 -translate-y-1/2 text-indigo-400"
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      required
                      value={formData.min_order_window}
                      onChange={(event)=>updateField(
                        'min_order_window',
                        event.target.value
                      )}
                      className="w-full rounded-2xl border border-transparent bg-white py-4 pl-12 pr-5 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="1"
                    />
                  </div>
                  <p className="mt-2 text-[10px] font-medium text-slate-400">
                    Days of advance notice needed to prepare an order.
                  </p>
                </div>

                <SellerAvailabilityEditor
                  weeklyAvailability={formData.weekly_availability}
                  blackoutDates={formData.blackout_dates}
                  onWeeklyAvailabilityChange={(value)=>updateField(
                    'weekly_availability',
                    value
                  )}
                  onBlackoutDatesChange={(value)=>updateField(
                    'blackout_dates',
                    value
                  )}
                />

                <SellerAvailabilityEditor
                  weeklyAvailability={formData.pickup_weekly_availability}
                  blackoutDates={formData.pickup_blackout_dates}
                  onWeeklyAvailabilityChange={(value)=>updateField(
                    'pickup_weekly_availability',
                    value
                  )}
                  onBlackoutDatesChange={(value)=>updateField(
                    'pickup_blackout_dates',
                    value
                  )}
                  title="Weekly pickup availability"
                  description="Set hours that apply only to pickup orders."
                  blackoutDescription="Buyers cannot choose these dates for pickup."
                />
              </div>
            </div>

            <div className="md:col-span-2">
              {hasPlanFeature('Custom shop URL') ? (
                <>
                  <label className="mb-3 ml-1 block text-xs font-black uppercase tracking-widest text-slate-400">
                    Unique Shop Username
                  </label>
                  <input
                    type="text"
                    value={formData.shop_username}
                    onChange={(event)=>updateField(
                      'shop_username',
                      normalizeShopUsername(event.target.value)
                    )}
                    minLength="3"
                    maxLength="30"
                    pattern="[a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9])?"
                    className="w-full rounded-[1.5rem] border border-transparent bg-slate-50 px-7 py-5 text-lg font-bold outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500"
                    placeholder="your-bakery-name"
                  />
                  {formData.shop_username && (
                    <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4">
                      <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-indigo-500">
                        Shop link preview
                      </p>
                      <p className="break-all text-sm font-black text-indigo-900">
                        {getShopUrl({...seller,shop_username:formData.shop_username})}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-sm font-black text-slate-800">Custom shop URL</p>
                  <p className="mt-1 text-xs font-medium text-slate-500">
                    Choose a plan that includes Custom shop URL to set a unique shop username.
                  </p>
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="mb-3 ml-1 block text-xs font-black uppercase tracking-widest text-slate-400">
                Business Description
              </label>
              <textarea
                required
                rows="5"
                value={formData.description}
                onChange={(event)=>updateField('description',event.target.value)}
                className="w-full resize-none rounded-[1.5rem] border border-transparent bg-slate-50 px-7 py-5 font-medium leading-relaxed outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500"
                placeholder="Share what makes your creations special..."
              />
            </div>
          </div>
        </div>

        <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 shadow-sm md:p-10">
          <MultiImageManager
            label="Showcase Photos"
            maxImages={5}
            path={getSellerPath(userId,'profile')}
            images={formData.profile_images}
            onImagesChange={(images)=>updateField('profile_images',images)}
            aspect={4 / 3}
            idealSize="800x600 (4:3)"
          />
        </div>

        <TaxInformationSection
          seller={seller}
          onSaved={refreshData}
        />

        <button
          type="submit"
          disabled={loading}
          className="flex w-full items-center justify-center rounded-[1.5rem] bg-indigo-600 px-8 py-5 text-sm font-black uppercase tracking-[0.2em] text-white shadow-2xl shadow-indigo-100 transition-all hover:bg-indigo-700 disabled:opacity-50"
        >
          <SafeIcon
            icon={loading ? FiLoader : FiSave}
            className={`mr-3 ${loading ? 'animate-spin' : ''}`}
          />
          {loading ? 'Saving Profile...' : 'Save Seller Profile'}
        </button>
      </form>
    </div>
  );
};

export default SellerProfileTab;
